import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { api, Post, resolveMediaUrl } from '../services/api';
import { TrinityGroup, TrinityPiece } from '../components/TrinityCard';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { SubmissionModal } from '../components/SubmissionModal';
import { TripleSplitPlayer } from '../components/TripleSplitPlayer';
import { Icon, IconName } from '../components/icons/Icon';
import { getCreatorTitle } from '../lib/titles';
import { LikeButton } from '../components/LikeButton';
import { FollowButton } from '../components/FollowButton';

// Raw child data from API
interface ChildPost {
    id: string;
    contentType: string;
    title: string;
    description: string;
    fileUrl: string;
    durationSec: number;
    likeCount: number;
    waveformPeaks?: string;
    authorName: string;
    authorIcon: string | null;
    textContent?: string;
}

type RoleKey = 'text' | 'image' | 'audio';
const ROLES: { key: RoleKey; icon: IconName; label: string; modal: 'script' | 'illustration' | 'voice'; bg: string }[] = [
    { key: 'text', icon: 'script', label: '台本', modal: 'script', bg: 'bg-role-script' },
    { key: 'image', icon: 'art', label: '絵', modal: 'illustration', bg: 'bg-role-illustration' },
    { key: 'audio', icon: 'voice', label: '声', modal: 'voice', bg: 'bg-role-voice' },
];

/**
 * PIPE-03 逆導線（作品→街区）: 作品詳細から街区へ持ち込み、共創の往復を作る（マスター§3）。
 * content_type ごとに**2導線まで**（意思決定4以内）。3役対等のため、どの型でも他2役の街区へ渡す形にする。
 * 役割色は識別専用（文字はダーク変種）。文脈は `?from_post=<id>` で引き継ぐ。
 */
const PIPE03: Record<RoleKey, { label: string; path: string; cls: string }[]> = {
    text: [
        { label: 'ステージで上演する', path: '/stage', cls: 'text-role-voice-text bg-role-voice-soft border-role-voice' },
        { label: '物語を継ぐ（サロンで下書き）', path: '/salon', cls: 'text-role-script-text bg-role-script-soft border-role-script' },
    ],
    image: [
        { label: 'この絵に物語を（サロン）', path: '/salon', cls: 'text-role-script-text bg-role-script-soft border-role-script' },
        { label: '声をあてる（ステージ）', path: '/stage', cls: 'text-role-voice-text bg-role-voice-soft border-role-voice' },
    ],
    audio: [
        { label: 'この声に絵を（トキワ荘）', path: '/tokiwa', cls: 'text-role-illustration-text bg-role-illustration-soft border-role-illustration' },
        { label: '台本を書く（サロン）', path: '/salon', cls: 'text-role-script-text bg-role-script-soft border-role-script' },
    ],
};

export const PostDetail = ({ params }: any) => {
    const postId = params?.id;
    const [post, setPost] = useState<Post | null>(null);
    const [children, setChildren] = useState<ChildPost[]>([]);
    const [trinityGroups, setTrinityGroups] = useState<TrinityGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalRole, setModalRole] = useState<'script' | 'illustration' | 'voice'>('voice');
    const [selectedGroup, setSelectedGroup] = useState<TrinityGroup | null>(null);
    const { playSound } = useGamenics();
    const [, setLocation] = useLocation(); // PIPE-03 逆導線の遷移に使用
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [myId, setMyId] = useState<string | null>(null);
    const [likedGenesis, setLikedGenesis] = useState(false);

    useEffect(() => {
        api.getCurrentUser().then(result => {
            if (result && result.success && result.data?.id) {
                setIsLoggedIn(true);
                setMyId(result.data.id);
            }
        });
    }, []);

    useEffect(() => {
        if (postId) refreshPost();
    }, [postId]);

    const refreshPost = () => {
        if (!postId) return;
        setLoading(true);
        api.getPostDetailById(postId).then(async (data) => {
            if (data) {
                setPost(data.post);
                api.getLikedIds([postId]).then(ids => setLikedGenesis(ids.includes(postId)));
                const mapped: ChildPost[] = await Promise.all(
                    data.children.map(async (c: any) => {
                        // API_BASE を前置した絶対URLにする。相対のままだと本番(別オリジン)で
                        // Pages のSPAフォールバックHTMLが返り、下の fetch がテキスト本文の代わりに
                        // index.html を読んでしまう（→ CLAUDE.md §4）
                        const fileUrl = resolveMediaUrl(c.file_url);

                        let textContent: string | undefined;
                        if (c.content_type === 'text' && fileUrl) {
                            try {
                                const res = await fetch(fileUrl);
                                if (res.ok) textContent = await res.text();
                            } catch (e) {
                                console.warn('Failed to fetch text content:', e);
                            }
                        }
                        return {
                            id: c.id,
                            contentType: c.content_type,
                            title: c.title,
                            description: c.description,
                            fileUrl,
                            durationSec: c.duration_sec || 0,
                            likeCount: c.like_count || 0,
                            waveformPeaks: c.waveform_peaks,
                            authorName: c.author?.name || 'Unknown',
                            authorIcon: c.author?.icon || null,
                            textContent,
                        };
                    })
                );
                setChildren(mapped);
                buildTrinityGroups(mapped);
            }
            setLoading(false);
        });
    };

    const buildTrinityGroups = (childPosts: ChildPost[]) => {
        const textPosts = childPosts.filter(c => c.contentType === 'text');
        const imagePosts = childPosts.filter(c => c.contentType === 'image');
        const audioPosts = childPosts.filter(c => c.contentType === 'audio');

        const toPiece = (c: ChildPost): TrinityPiece => ({
            id: c.id,
            authorName: c.authorName,
            authorIcon: c.authorIcon,
            fileUrl: c.fileUrl,
            description: c.description,
            durationSec: c.durationSec,
            likeCount: c.likeCount,
            textContent: c.textContent,
        });

        if (textPosts.length > 0) {
            setTrinityGroups(textPosts.map((text) => ({
                text: toPiece(text),
                image: imagePosts[0] ? toPiece(imagePosts[0]) : undefined,
                audio: audioPosts[0] ? toPiece(audioPosts[0]) : undefined,
            })));
        } else if (imagePosts.length > 0 || audioPosts.length > 0) {
            setTrinityGroups([{
                text: undefined,
                image: imagePosts[0] ? toPiece(imagePosts[0]) : undefined,
                audio: audioPosts[0] ? toPiece(audioPosts[0]) : undefined,
            }]);
        } else {
            setTrinityGroups([]);
        }
    };

    const seatStatus = (role: RoleKey) => {
        if (!post?.roleLimits || !post?.childCounts) return { full: false, label: '募集中！' };
        const limit = post.roleLimits[role];
        const count = post.childCounts[role];
        if (limit === 0) return { full: true, label: '募集なし' };
        if (count >= limit) return { full: true, label: '満席' };
        return { full: false, label: '募集中！' };
    };

    const openModal = (modal: 'script' | 'illustration' | 'voice') => {
        playSound(GamenicsEvent.TAP_CONFIRM);
        setModalRole(modal);
        setIsModalOpen(true);
    };

    if (loading && !post) return <div className="min-h-[60vh] flex items-center justify-center text-primary font-display font-bold animate-pulse">{COPY.loadingThemes}</div>;
    if (!post && !loading) return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-ink-3 font-display text-xl">作品が見つかりませんでした...</div>;
    if (!post) return null;

    // 画像作品は画像を、テキスト/音声の「お題」はテキストパネルを主役にする
    const heroImg = post.contentType === 'image' ? resolveMediaUrl(post.imageUrl || post.fileUrl) : null;

    return (
        <div className="max-w-5xl mx-auto px-6 pt-8 pb-16">
            {isModalOpen && (
                <SubmissionModal
                    postId={post.id}
                    role={modalRole}
                    existingChildren={children}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={refreshPost}
                />
            )}

            {/* ── Breadcrumb ── */}
            <a href="/" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary mb-5 hover:underline">
                <Icon name="arrow-right" size={16} className="rotate-180" /> 作品を探す
            </a>

            {/* ── Work hero ── */}
            <div className="grid md:grid-cols-2 gap-5 mb-14">
                {/* Left: content */}
                <div className="pop-card overflow-hidden">
                    {heroImg ? (
                        <img src={heroImg} alt={post.title} className="w-full h-full max-h-[340px] object-cover" />
                    ) : (
                        <div className="bg-sun border-b-2 border-ink p-6 min-h-[240px] flex flex-col gap-5">
                            <div className="flex flex-wrap gap-2">
                                <span className="pop-tag">{post.title ? '起点の物語' : '共創'}</span>
                            </div>
                            <p className="font-display font-bold text-xl text-ink leading-relaxed">
                                {post.description || 'この物語の続きを、あなたの役割で紡ごう。'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Right: meta + seats */}
                <div className="flex flex-col gap-4">
                    <div className="bg-surface border-2 border-ink rounded-pop-md shadow-hard-sm p-5">
                        <h1 className="font-display font-black text-2xl leading-snug mb-3 text-ink">{post.title}</h1>
                        <div className="flex items-center gap-2.5 text-sm font-bold text-ink">
                            <div className="w-8 h-8 rounded-full border-2 border-ink overflow-hidden bg-cream-2">
                                {post.creatorIcon ? <img src={post.creatorIcon} alt={post.creatorName} className="w-full h-full object-cover" /> : null}
                            </div>
                            <span className="truncate">{post.creatorName}</span>
                            <span className={`text-[10px] font-display font-bold px-2 py-0.5 rounded-full border-2 border-ink flex-shrink-0 ${getCreatorTitle(post.creatorLevel || 1).cls}`}>{getCreatorTitle(post.creatorLevel || 1).name}</span>
                            <span className="ml-auto"><FollowButton creatorId={post.creatorId} myId={myId} isLoggedIn={isLoggedIn} /></span>
                        </div>
                        <div className="flex items-center justify-between border-t-2 border-cream-2 mt-4 pt-4">
                            <span className="text-xs text-ink-3 font-bold">この作品が好きなら</span>
                            <LikeButton
                                submissionId={post.id}
                                initialCount={post.likeCount || 0}
                                authorId={post.creatorId}
                                myId={myId}
                                isLoggedIn={isLoggedIn}
                                initialLiked={likedGenesis}
                            />
                        </div>
                    </div>

                    <div className="bg-surface border-2 border-ink rounded-pop-md shadow-hard-sm p-5">
                        <div className="font-display font-bold text-sm mb-3 text-ink">三位一体の空席</div>
                        <div className="grid grid-cols-3 gap-2.5">
                            {ROLES.map((r) => {
                                const st = seatStatus(r.key);
                                return (
                                    <button
                                        key={r.key}
                                        disabled={st.full}
                                        onClick={() => openModal(r.modal)}
                                        className={`border-2 border-ink rounded-pop-sm p-3 text-center transition-transform ${st.full ? 'bg-cream-2 opacity-70 cursor-default' : 'bg-surface shadow-hard-sm hover:-translate-y-0.5 cursor-pointer'}`}
                                    >
                                        <span className={`w-9 h-9 rounded-pop-sm border-2 border-ink ${r.bg} text-white flex items-center justify-center mx-auto mb-2`}>
                                            <Icon name={r.icon} size={18} />
                                        </span>
                                        <b className="font-display text-[13px] block text-ink">{r.label}</b>
                                        <small className={`text-[11px] ${st.full ? 'text-ink-3' : 'text-pop font-bold'}`}>{st.label}</small>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[12px] text-ink-3 mt-3 text-center">空いている役割を選んで、共創に参加しよう</p>
                    </div>

                    {/* ── PIPE-03: 街区へ持ち込む（逆導線・content_type別に2つまで） ── */}
                    <div className="bg-surface border-2 border-ink rounded-pop-md shadow-hard-sm p-5">
                        <div className="font-display font-bold text-sm mb-1 text-ink">この作品を街区へ持ち込む</div>
                        <p className="text-[12px] text-ink-3 mb-3">続きは、三役の街で。</p>
                        <div className="flex flex-col gap-2">
                            {(PIPE03[(post.contentType as RoleKey)] ?? PIPE03.text).map((l) => (
                                <button
                                    key={l.path + l.label}
                                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`${l.path}?from_post=${encodeURIComponent(post.id)}`); }}
                                    className={`w-full min-h-[44px] px-4 py-2.5 rounded-pop-sm border-2 font-bold text-[13px] text-left flex items-center gap-2 transition-transform hover:-translate-y-0.5 ${l.cls}`}
                                >
                                    <span className="flex-1">{l.label}</span>
                                    <Icon name="arrow-right" size={15} />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Co-creations ── */}
            <h2 className="font-display font-black text-2xl text-center text-ink mb-1">この物語から生まれた共創</h2>
            <p className="text-center text-pop font-bold tracking-[0.16em] text-sm mb-7">CO-CREATIONS</p>

            {trinityGroups.length === 0 ? (
                <div className="bg-surface border-2 border-dashed border-ink/40 rounded-pop text-center py-16 px-6">
                    <p className="text-ink-2 mb-4">まだ共創がありません。最初の一人になろう！</p>
                    <button className="btn btn-primary" onClick={() => openModal('script')}>
                        <Icon name="plus" size={18} /> {COPY.start}
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {trinityGroups.map((group, i) => {
                        const grpRoles: { p?: TrinityPiece; ic: IconName; bg: string }[] = [
                            { p: group.text, ic: 'script', bg: 'bg-role-script' },
                            { p: group.image, ic: 'art', bg: 'bg-role-illustration' },
                            { p: group.audio, ic: 'voice', bg: 'bg-role-voice' },
                        ];
                        const author = group.audio?.authorName || group.text?.authorName || group.image?.authorName || 'Unknown';
                        return (
                            <button
                                key={i}
                                onClick={() => setSelectedGroup(group)}
                                className="pop-card overflow-hidden text-left hover:-translate-y-1 transition-transform cursor-pointer block w-full"
                            >
                                <div className="h-40 border-b-2 border-ink overflow-hidden">
                                    {group.image?.fileUrl ? (
                                        <img src={group.image.fileUrl} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="bg-sun h-full p-4 font-display font-bold text-ink text-sm leading-relaxed overflow-hidden">
                                            {(group.text?.textContent || group.text?.description || 'この物語の続き')?.slice(0, 80)}
                                        </div>
                                    )}
                                </div>
                                <div className="p-4">
                                    <div className="flex gap-1.5 mb-2.5">
                                        {grpRoles.map((x, j) => (
                                            <span key={j} className={`w-6 h-6 rounded-full border-2 border-ink flex items-center justify-center ${x.p ? `${x.bg} text-white` : 'bg-white text-ink-3 opacity-50'}`}>
                                                <Icon name={x.ic} size={12} />
                                            </span>
                                        ))}
                                    </div>
                                    <h4 className="font-display font-bold text-base mb-2 line-clamp-1 text-ink">{group.text?.description || '無題の共創'}</h4>
                                    <div className="flex items-center justify-between border-t-2 border-cream-2 pt-2.5">
                                        <span className="text-xs font-bold text-ink-2">{author}</span>
                                        <span className="btn btn-primary btn-sm">鑑賞する <Icon name="arrow-right" size={14} /></span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ── TripleSplitPlayer Modal ── */}
            {selectedGroup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 overflow-y-auto">
                    <div className="relative w-full max-w-5xl bg-surface rounded-pop border-2 border-ink shadow-hard my-8">
                        <button
                            onClick={() => setSelectedGroup(null)}
                            aria-label="閉じる"
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-cream-2 border-2 border-ink hover:bg-sun flex items-center justify-center z-50"
                        >
                            <Icon name="x" size={18} />
                        </button>
                        <div className="p-4 sm:p-8 max-h-[90vh] overflow-y-auto">
                            <TripleSplitPlayer
                                workId={selectedGroup.audio?.id || selectedGroup.image?.id || selectedGroup.text?.id || ''}
                                submissionId={post?.id || ''}
                                audioUrl={selectedGroup.audio?.fileUrl}
                                imageUrl={selectedGroup.image?.fileUrl}
                                textContent={selectedGroup.text?.textContent || selectedGroup.text?.description}
                                title={post?.title || '三位一体'}
                                isLoggedIn={isLoggedIn}
                                onClose={() => setSelectedGroup(null)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
